import { useState } from "react";
import { FormatSelection } from "./format-selection";
import { ScopeSelection, type ExportScope } from "./scope-selection";
import { ValidationStep } from "./validation-step";
import { ExportGeneration } from "./export-generation";

export const ExportWizard = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedFormat, setSelectedFormat] = useState("bexio");
  const [exportScope, setExportScope] = useState<ExportScope | null>(null);

  const steps = [
    { number: 1, title: "Format", active: currentStep === 1 },
    { number: 2, title: "Scope", active: currentStep === 2 },
    { number: 3, title: "Validate", active: currentStep === 3 },
    { number: 4, title: "Export", active: currentStep === 4 }
  ];

  const handleFormatNext = (format: string) => {
    setSelectedFormat(format);
    setCurrentStep(2);
  };

  const handleScopeNext = (scope: ExportScope) => {
    setExportScope(scope);
    setCurrentStep(3);
  };

  const handleComplete = () => {
    // Reset wizard
    setCurrentStep(1);
    setSelectedFormat("bexio");
    setExportScope(null);
  };

  return (
    <section className="bg-white p-6 rounded-2xl border border-brand-border">
      {/* Step Indicators */}
      <div className="flex items-center justify-between mb-header-mb max-w-4xl mx-auto">
        {steps.map((step, index) => (
          <div key={step.number} className="relative flex-1 flex items-center">
            <div className="flex flex-col items-center text-center w-full">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold z-10 ${
                step.active
                  ? 'bg-brand-primary text-white'
                  : currentStep > step.number
                  ? 'bg-green-600 text-white'
                  : 'bg-brand-secondary text-brand-primary'
              }`}>
                {currentStep > step.number ? (
                  <i className="fa-solid fa-check"></i>
                ) : (
                  step.number
                )}
              </div>
              <p className={`mt-2 text-sm font-medium ${
                step.active
                  ? 'text-brand-primary'
                  : 'text-brand-text-secondary'
              }`}>
                {step.title}
              </p>
            </div>
            {index < steps.length - 1 && (
              <div className={`flex-1 h-0.5 absolute top-5 left-1/2 right-0 ${
                currentStep > step.number ? 'bg-green-600' : 'bg-brand-border'
              }`}></div>
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <FormatSelection
          onNext={handleFormatNext}
        />
      )}
      {currentStep === 2 && (
        <ScopeSelection
          onNext={handleScopeNext}
          onBack={() => setCurrentStep(1)}
        />
      )}
      {currentStep === 3 && exportScope && (
        <ValidationStep
          scope={exportScope}
          selectedFormat={selectedFormat}
          onNext={() => setCurrentStep(4)}
          onBack={() => setCurrentStep(2)}
        />
      )}
      {currentStep === 4 && exportScope && (
        <ExportGeneration
          scope={exportScope}
          selectedFormat={selectedFormat}
          onBack={() => setCurrentStep(3)}
          onComplete={handleComplete}
        />
      )}
    </section>
  );
};
